import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, ShieldOff, ShieldCheck, AlertTriangle, Trash2, Users as UsersIcon, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminBusinesses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { biz, action: 'disable' | 'enable' | 'purge' }
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/businesses");
      setItems(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load businesses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const onConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    const { biz, action } = confirm;
    try {
      if (action === "disable") {
        await api.post(`/admin/businesses/${biz.id}/disable`);
        toast.success(`Disabled ${biz.name}. Data retained for 30 days.`);
      } else if (action === "enable") {
        await api.post(`/admin/businesses/${biz.id}/enable`);
        toast.success(`Re-enabled ${biz.name}.`);
      } else if (action === "purge") {
        await api.delete(`/admin/businesses/${biz.id}`);
        toast.success(`Permanently deleted ${biz.name} and all its data.`);
      }
      setConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const activeCount = items.filter((b) => !b.disabled).length;
  const disabledCount = items.filter((b) => b.disabled).length;

  return (
    <div className="p-10 max-w-[1400px]" data-testid="admin-businesses-page">
      <PageHeader overline="Platform" title="All Businesses" testid="admin-businesses-header" />

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-[10px] mb-6">
        <div className="bg-white border border-[#E3E3E3] p-4">
          <div className="overline">Total Workspaces</div>
          <div className="font-display text-2xl font-bold text-[#0F172A] mt-1" data-testid="stat-total">{items.length}</div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-4">
          <div className="overline">Active</div>
          <div className="font-display text-2xl font-bold text-[#10B981] mt-1" data-testid="stat-active">{activeCount}</div>
        </div>
        <div className="bg-white border border-[#E3E3E3] p-4">
          <div className="overline">Disabled (retention)</div>
          <div className="font-display text-2xl font-bold text-[#EF4444] mt-1" data-testid="stat-disabled">{disabledCount}</div>
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white overflow-x-auto" data-testid="admin-businesses-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left px-4 py-3 overline">Workspace</th>
              <th className="text-left px-4 py-3 overline">Location</th>
              <th className="text-left px-4 py-3 overline">Currency</th>
              <th className="text-left px-4 py-3 overline">Admin(s)</th>
              <th className="text-left px-4 py-3 overline">Created</th>
              <th className="text-right px-4 py-3 overline">Users</th>
              <th className="text-right px-4 py-3 overline">Entries</th>
              <th className="text-left px-4 py-3 overline">Status</th>
              <th className="px-4 py-3 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-[#6B7280]" data-testid="admin-loading">Loading workspaces…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-[#6B7280]" data-testid="admin-empty">No businesses yet.</td></tr>
            )}
            {!loading && items.map((b) => {
              const isMine = b.is_my_business;
              return (
                <tr key={b.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`biz-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#6B7280] shrink-0" />
                      <div>
                        <div className="font-medium text-[#111827]" data-testid={`biz-name-${b.id}`}>{b.name || <em className="text-[#EF4444]">(unnamed)</em>}</div>
                        {isMine && <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-semibold mt-0.5">App Owner</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]" data-testid={`biz-location-${b.id}`}>
                    {(b.city || b.country) ? (
                      <div className="leading-tight">
                        {b.city && <div className="text-[#111827]">{b.city}</div>}
                        {b.country && <div className="text-[#6B7280]">{b.country}</div>}
                      </div>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`biz-currency-${b.id}`}>
                    {b.currency_code ? (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[#111827]">
                        <span className="text-base">{b.currency_symbol || ""}</span>
                        <span className="font-semibold">{b.currency_code}</span>
                      </span>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">
                    {b.admins && b.admins.length > 0 ? (
                      <div className="space-y-0.5">
                        {b.admins.map((a) => (
                          <div key={a.id} className="flex items-center gap-1.5">
                            <span className="font-mono">{a.email}</span>
                            <span className="text-[9px] uppercase tracking-wider bg-[#F3F4F6] px-1 py-0.5 text-[#6B7280]">{a.role}</span>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    <span className="inline-flex items-center gap-1 text-[#4B5563]"><UsersIcon className="h-3 w-3" />{b.users_count}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    <span className="inline-flex items-center gap-1 text-[#4B5563]"><FileText className="h-3 w-3" />{b.entries_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    {b.disabled ? (
                      <div data-testid={`biz-status-${b.id}`}>
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30">
                          Disabled
                        </span>
                        <div className="text-[10px] text-[#6B7280] mt-1">Auto-purge: {fmtDate(b.purge_at)}</div>
                      </div>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30" data-testid={`biz-status-${b.id}`}>
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {!isMine && !b.disabled && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setConfirm({ biz: b, action: "disable" })}
                          data-testid={`disable-biz-${b.id}`}
                          className="rounded-none h-8 text-[11px] uppercase tracking-wider font-semibold border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
                        >
                          <ShieldOff className="h-3 w-3 mr-1.5" /> Disable
                        </Button>
                      )}
                      {!isMine && b.disabled && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setConfirm({ biz: b, action: "enable" })}
                            data-testid={`enable-biz-${b.id}`}
                            className="rounded-none h-8 text-[11px] uppercase tracking-wider font-semibold border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10 hover:text-[#10B981]"
                          >
                            <ShieldCheck className="h-3 w-3 mr-1.5" /> Restore
                          </Button>
                          <button
                            type="button"
                            onClick={() => setConfirm({ biz: b, action: "purge" })}
                            data-testid={`purge-biz-${b.id}`}
                            title="Permanently delete now"
                            className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {isMine && <span className="text-xs text-[#9CA3AF]">—</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-[#6B7280] flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#F59E0B]" />
        <p>
          Disabling a workspace immediately blocks login and API access for all of its users.
          Their data is retained for <strong>30 days</strong> after which it is permanently purged.
          Restore at any time before the purge date to bring the workspace back online.
        </p>
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">
              {confirm?.action === "disable" && "Disable workspace?"}
              {confirm?.action === "enable" && "Restore workspace?"}
              {confirm?.action === "purge" && "Permanently delete?"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              {confirm?.action === "disable" && `All users of "${confirm?.biz?.name}" will be blocked from logging in. Data is preserved for 30 days and can be restored any time before then.`}
              {confirm?.action === "enable" && `Re-enable "${confirm?.biz?.name}" so its users can log in again. Data is unchanged.`}
              {confirm?.action === "purge" && `Permanently delete "${confirm?.biz?.name}" and ALL its users, entries, parties, and categories. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setConfirm(null)} disabled={busy}
              data-testid="confirm-cancel"
              className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
            <Button
              type="button" onClick={onConfirm} disabled={busy}
              data-testid="confirm-ok"
              className={`rounded-none h-10 text-xs uppercase tracking-wider ${
                confirm?.action === "enable"
                  ? "bg-[#10B981] hover:bg-[#059669] text-white"
                  : "bg-[#EF4444] hover:bg-[#DC2626] text-white"
              }`}
            >
              {busy ? "Working…" : (
                confirm?.action === "disable" ? "Disable workspace" :
                confirm?.action === "enable" ? "Restore workspace" :
                "Delete forever"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
